import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../services/api.service';

interface NeonRow {
  [key: string]: unknown;
}

interface ColumnSummary {
  key: string;
  numeric: boolean;
  count: number;
  sum: number;
  min: number;
  max: number;
  average: number;
}

@Component({
  selector: 'app-neon-data',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="neon-explorer" aria-labelledby="neon-title">
      <header class="explorer-header">
        <div>
          <p class="eyebrow">Database workspace</p>
          <h1 id="neon-title">Neon data explorer</h1>
          <p class="intro">Browse live tables without exposing protected fields.</p>
        </div>
        <div class="connection-status">
          <span class="status-dot" aria-hidden="true"></span>
          Connected
        </div>
      </header>

      <div class="control-bar">
        <div class="filter-controls">
          <div class="field-group table-field">
            <label for="table-select">Active table</label>
            <select
              id="table-select"
              [value]="selectedTable"
              [disabled]="loading"
              (change)="changeTable($any($event.target).value)"
            >
              <option value="users">Users</option>
              <option value="employees">Employees</option>
              <option value="city">Cities</option>
            </select>
          </div>
          <div class="field-group search-field">
            <label for="row-search">Search records</label>
            <input
              id="row-search"
              type="search"
              placeholder="Filter any value"
              [value]="searchTerm"
              (input)="setSearchTerm($any($event.target).value)"
            />
          </div>
        </div>
        <button class="refresh-button" type="button" (click)="loadData()" [disabled]="loading">
          {{ loading ? 'Refreshing...' : 'Refresh data' }}
        </button>
      </div>

      <p class="error-message" *ngIf="errorMessage" role="alert">{{ errorMessage }}</p>

      <div class="loading-overlay" *ngIf="loading && rows.length" role="status" aria-live="polite">
        <span class="loading-mark" aria-hidden="true"></span>
        Loading {{ selectedTable }} records...
      </div>

      <div class="table-shell" *ngIf="rows.length; else emptyState">
        <div class="table-summary">
          <span><strong>{{ filteredRows.length }}</strong> {{ filteredRows.length === 1 ? 'record' : 'records' }} in {{ formatHeader(loadedTable) }}</span>
          <div class="table-actions">
            <label class="page-size-control" for="page-size">
              Rows per page
              <select id="page-size" [value]="pageSize" (change)="setPageSize($any($event.target).value)">
                <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
              </select>
            </label>
            <div class="aggregate-actions">
              <span>{{ selectedAggregateColumns.length }} selected</span>
              <button type="button" (click)="showAggregates()" [disabled]="!selectedAggregateColumns.length">Get aggregate</button>
              <button type="button" class="clear-aggregate-button" (click)="clearAggregates()" [disabled]="!selectedAggregateColumns.length && !appliedAggregateColumns.length">Clear aggregate</button>
              <button class="scroll-arrow bottom-arrow" type="button" (click)="scrollTo(tableBottom)" aria-label="Go to bottom" title="Go to bottom"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14"></path></svg><span>Bottom</span></button>
            </div>
          </div>
        </div>
        <div class="aggregate-scroll" *ngIf="selectedColumnSummaries.length" aria-label="Selected column summaries">
          <article class="aggregate-card" *ngFor="let summary of selectedColumnSummaries">
            <span class="aggregate-title">{{ formatHeader(summary.key) }}</span>
            <ng-container *ngIf="summary.numeric; else textAggregate">
              <strong>{{ formatNumber(summary.sum) }}</strong>
              <span class="aggregate-label">Sum</span>
              <div class="aggregate-values">
                <span>Min <b>{{ formatNumber(summary.min) }}</b></span>
                <span>Max <b>{{ formatNumber(summary.max) }}</b></span>
                <span>Avg <b>{{ formatNumber(summary.average) }}</b></span>
              </div>
            </ng-container>
            <ng-template #textAggregate>
              <strong>{{ summary.count }}</strong>
              <span class="aggregate-label">Non-empty values</span>
            </ng-template>
          </article>
        </div>
        <div #tableTop class="table-scroll">
          <table>
            <thead>
              <tr>
                <th *ngFor="let key of headers" [style.min-width.px]="getColumnMinWidth(key)">
                  <span class="column-header">
                    <input
                      class="column-checkbox"
                      type="checkbox"
                      [id]="'aggregate-' + key"
                      [checked]="isColumnSelected(key)"
                      (change)="toggleColumnSelection(key)"
                      [attr.aria-label]="'Select ' + formatHeader(key) + ' for aggregates'"
                    />
                    <button class="sort-button" type="button" (click)="sortBy(key)" [attr.aria-label]="'Sort by ' + formatHeader(key)">
                      {{ formatHeader(key) }} <span aria-hidden="true">{{ getSortIndicator(key) }}</span>
                    </button>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of paginatedRows">
                <td *ngFor="let key of headers" [style.min-width.px]="getColumnMinWidth(key)">
                  {{ formatValue(row[key]) }}
                </td>
              </tr>
              <tr *ngIf="!filteredRows.length">
                <td class="no-results" [attr.colspan]="headers.length">No records match your search.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="table-footer" *ngIf="filteredRows.length > pageSize">
          <span>Showing {{ pageStart }}-{{ pageEnd }} of {{ filteredRows.length }}</span>
          <div class="pagination" aria-label="Table pagination">
            <button type="button" aria-label="Previous page" (click)="previousPage()" [disabled]="currentPage === 1">Previous</button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button type="button" aria-label="Next page" (click)="nextPage()" [disabled]="currentPage === totalPages">Next</button>
          </div>
        </footer>
        <div #tableBottom class="table-navigation-bottom">
          <button class="scroll-arrow top-arrow" type="button" (click)="scrollTo(tableTop)" aria-label="Go to top" title="Go to top"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21V9m0 0 4 4m-4-4-4 4M5 3h14"></path></svg><span>Top</span></button>
        </div>
      </div>

      <ng-template #emptyState>
        <div class="empty-state" *ngIf="loading">
          <span class="loading-mark" aria-hidden="true"></span>
          Loading {{ selectedTable }} records...
        </div>
        <div class="empty-state" *ngIf="!loading && !errorMessage">
          <strong>No records found</strong>
          <span>This table does not have data to display yet.</span>
        </div>
      </ng-template>
    </section>
  `,
  styles: [`
    :host { display: block; }
    .neon-explorer { background: #fff; border: 1px solid #dfe4ed; border-radius: 8px; box-shadow: 0 10px 30px rgba(19, 31, 57, .07); overflow: hidden; }
    .explorer-header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; padding: 30px 32px 24px; border-bottom: 1px solid #e8ebf0; background: linear-gradient(115deg, #f8fbff 0%, #fff 55%, #f6fffc 100%); }
    .eyebrow { margin: 0 0 8px; color: #087b63; font-size: .72rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
    h1 { margin: 0; color: #17223b; font-size: 1.7rem; letter-spacing: 0; }
    .intro { margin: 8px 0 0; color: #637087; font-size: .93rem; }
    .connection-status { display: inline-flex; flex-shrink: 0; align-items: center; gap: 7px; padding: 7px 10px; border: 1px solid #bfe6d9; border-radius: 4px; color: #087b63; background: #effbf6; font-size: .8rem; font-weight: 700; }
    .status-dot { width: 7px; height: 7px; border-radius: 50%; background: #0fa77f; }
    .control-bar { display: flex; gap: 16px; align-items: end; padding: 22px 32px; border-bottom: 1px solid #e8ebf0; }
    .filter-controls { display: grid; grid-template-columns: minmax(130px, .75fr) minmax(200px, 1.5fr); flex: 1; gap: 16px; min-width: 0; }
    .field-group { min-width: 0; }
    label { display: block; margin: 0 0 7px; color: #53617a; font-size: .78rem; font-weight: 700; }
    select, input { width: 100%; height: 40px; padding: 0 11px; border: 1px solid #cfd6e1; border-radius: 5px; color: #1b2842; background: #fff; font: inherit; font-size: .9rem; }
    select:focus, input:focus { outline: 2px solid rgba(15, 167, 127, .2); outline-offset: 1px; border-color: #0fa77f; }
    .refresh-button { height: 40px; padding: 0 16px; border: 1px solid #087b63; border-radius: 5px; color: #fff; background: #087b63; font-weight: 700; white-space: nowrap; }
    .refresh-button:hover:not(:disabled) { background: #066650; opacity: 1; }
    button:disabled { cursor: not-allowed; opacity: .55; }
    .error-message { margin: 18px 32px 0; padding: 10px 12px; border-left: 3px solid #c63e48; color: #9e2630; background: #fff5f5; font-size: .88rem; }
    .table-summary { display: flex; justify-content: space-between; gap: 16px; align-items: center; padding: 14px 32px; color: #68758b; font-size: .82rem; }
    .table-summary strong { color: #1c2943; }
    .table-actions { display: flex; gap: 16px; align-items: center; }
    .page-size-control { display: inline-flex; gap: 7px; align-items: center; margin: 0; color: #68758b; font-size: .76rem; white-space: nowrap; }
    .page-size-control select { width: auto; height: 31px; padding: 0 25px 0 8px; margin: 0; font-size: .78rem; }
    .aggregate-actions { display: flex; gap: 10px; align-items: center; }
    .aggregate-actions button { padding: 7px 10px; border: 1px solid #087b63; border-radius: 4px; color: #087b63; background: #fff; font-size: .76rem; font-weight: 700; }
    .aggregate-actions button:hover:not(:disabled) { color: #fff; background: #087b63; opacity: 1; }
    .aggregate-actions .clear-aggregate-button { border-color: #b86b47; color: #9a4e2d; }
    .aggregate-actions .clear-aggregate-button:hover:not(:disabled) { background: #9a4e2d; color: #fff; }
    .aggregate-scroll { display: flex; gap: 10px; padding: 0 32px 16px; overflow-x: auto; }
    .aggregate-card { display: flex; min-width: 174px; flex: 1 0 174px; flex-direction: column; gap: 3px; padding: 12px; border: 1px solid #d7e8e3; border-radius: 5px; background: #f6fcfa; }
    .aggregate-title { overflow: hidden; color: #506b66; font-size: .7rem; font-weight: 700; letter-spacing: .06em; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
    .aggregate-card > strong { color: #075e4c; font-size: 1.05rem; }
    .aggregate-label { color: #617672; font-size: .72rem; }
    .aggregate-values { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px; margin-top: 5px; padding-top: 7px; border-top: 1px solid #dcece8; color: #69807b; font-size: .67rem; }
    .aggregate-values b { display: block; margin-top: 2px; color: #284a43; font-size: .72rem; }
    .table-scroll { overflow-x: auto; border-top: 1px solid #d8e5e4; }
    .scroll-arrow { display: inline-flex; height: 31px; gap: 5px; align-items: center; padding: 0 9px; border: 1px solid #a9d8cb; border-radius: 4px; color: #076250; background: #fff; font-size: .76rem; font-weight: 700; line-height: 1; }
    .scroll-arrow svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2; }
    .bottom-arrow { border-color: #b78936; color: #8d620e; background: #fff9ea; }
    .bottom-arrow:hover { color: #fff; background: #8d620e; opacity: 1; }
    .top-arrow { border-color: #b78936; color: #8d620e; background: #fff9ea; }
    .top-arrow:hover { color: #fff; background: #8d620e; opacity: 1; }
    .table-navigation-bottom { display: flex; justify-content: center; padding: 10px 32px; border-top: 1px solid #e2eeeb; background: #fbfdfd; }
    table { width: 100%; border-collapse: collapse; font-size: .88rem; }
    th { padding: 13px 16px; border-bottom: 1px solid #076250; color: #f4fffc; background: #087b63; font-size: .72rem; font-weight: 700; letter-spacing: .06em; text-align: left; text-transform: uppercase; white-space: nowrap; }
    th:first-child { box-shadow: inset 4px 0 #26c59b; }
    .column-header { display: inline-flex; gap: 8px; align-items: center; }
    .column-checkbox { width: 14px; height: 14px; margin: 0; accent-color: #26c59b; cursor: pointer; }
    .sort-button { display: inline-flex; gap: 6px; align-items: center; padding: 0; color: inherit; background: transparent; font: inherit; text-align: inherit; text-transform: inherit; }
    .sort-button:hover { color: #bff7e3; opacity: 1; }
    td { max-width: 340px; padding: 13px 16px; border-bottom: 1px solid #e2eeeb; color: #263d4a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    tbody tr:nth-child(even) { background: #f5faf9; }
    tbody tr:hover { background: #dff5ed; }
    tbody tr:hover td:first-child { color: #075e4c; font-weight: 700; }
    .no-results { padding: 28px 16px; color: #68758b; text-align: center; }
    .table-footer { display: flex; justify-content: space-between; gap: 16px; align-items: center; padding: 14px 32px; color: #68758b; font-size: .82rem; }
    .pagination { display: flex; align-items: center; gap: 10px; }
    .pagination button { padding: 6px 10px; border: 1px solid #cfd6e1; border-radius: 4px; color: #33425d; background: #fff; font-size: .78rem; }
    .pagination button:hover:not(:disabled) { border-color: #087b63; color: #087b63; opacity: 1; }
    .empty-state { display: flex; min-height: 220px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #748197; font-size: .9rem; text-align: center; }
    .empty-state strong { color: #273550; font-size: 1rem; }
    .loading-mark { width: 24px; height: 24px; border: 3px solid #d8eee8; border-top-color: #0fa77f; border-radius: 50%; animation: spin .75s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .loading-overlay { display: flex; gap: 10px; align-items: center; justify-content: center; padding: 14px 32px; color: #087b63; background: #f2fbf8; font-size: .88rem; font-weight: 700; }
    @media (max-width: 680px) { .explorer-header, .control-bar, .table-footer, .table-navigation-bottom { padding-left: 18px; padding-right: 18px; } .explorer-header { flex-direction: column; gap: 16px; } .control-bar { gap: 10px; } .filter-controls { grid-template-columns: minmax(76px, .8fr) minmax(0, 1.2fr); gap: 8px; } .refresh-button { flex: 0 0 auto; padding-left: 10px; padding-right: 10px; } .table-summary, .aggregate-scroll { padding-left: 18px; padding-right: 18px; } .table-summary, .table-actions { align-items: flex-start; flex-direction: column; } .table-actions { gap: 10px; } .table-footer { align-items: flex-start; flex-direction: column; } }
  `],
})
export class NeonDataComponent implements OnInit {
  rows: NeonRow[] = [];
  headers: string[] = [];
  loading = false;
  errorMessage = '';
  selectedTable = 'users';
  loadedTable = '';
  searchTerm = '';
  currentPage = 1;
  pageSize = 25;
  readonly pageSizeOptions = [25, 50, 100, 200, 500];
  sortColumn = '';
  sortDirection: 'ascending' | 'descending' = 'ascending';
  selectedAggregateColumns: string[] = [];
  appliedAggregateColumns: string[] = [];
  private latestRequestId = 0;

  get filteredRows(): NeonRow[] {
    const query = this.searchTerm.trim().toLowerCase();
    const matchingRows = !query
      ? this.rows
      : this.rows.filter((row) =>
          Object.values(row).some((value) => this.formatValue(value).toLowerCase().includes(query))
        );

    if (!this.sortColumn) {
      return matchingRows;
    }

    return [...matchingRows].sort((left, right) => this.compareValues(left[this.sortColumn], right[this.sortColumn]));
  }

  get columnSummaries(): ColumnSummary[] {
    return this.headers.map((key) => {
      const values = this.filteredRows
        .map((row) => row[key])
        .filter((value) => value !== null && value !== undefined && value !== '');
      const numericValues = values.map((value) => this.toNumber(value));
      const isNumeric = values.length > 0 && numericValues.every((value) => value !== null);

      if (!isNumeric) {
        return { key, numeric: false, count: values.length, sum: 0, min: 0, max: 0, average: 0 };
      }

      const numbers = numericValues as number[];
      const sum = numbers.reduce((total, value) => total + value, 0);
      return {
        key,
        numeric: true,
        count: numbers.length,
        sum,
        min: Math.min(...numbers),
        max: Math.max(...numbers),
        average: sum / numbers.length,
      };
    });
  }

  get selectedColumnSummaries(): ColumnSummary[] {
    return this.columnSummaries.filter((summary) => this.appliedAggregateColumns.includes(summary.key));
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
  }

  get paginatedRows(): NeonRow[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredRows.slice(startIndex, startIndex + this.pageSize);
  }

  get pageStart(): number {
    return this.filteredRows.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredRows.length);
  }

  private isSensitiveField(fieldName: string): boolean {
    return fieldName.toLowerCase().includes('password');
  }

  private sanitizeRows(data: NeonRow[]): NeonRow[] {
    return data.map((row) => {
      const filteredEntries = Object.entries(row).filter(
        ([key]) => !this.isSensitiveField(key)
      );
      return Object.fromEntries(filteredEntries);
    });
  }

  getColumnMinWidth(fieldName: string): number | null {
    const normalized = fieldName.toLowerCase();
    if (normalized === 'first_name' || normalized === 'firstname') {
      return 180;
    }
    return null;
  }

  formatHeader(fieldName: string): string {
    return fieldName.replace(/_/g, ' ');
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }

  isColumnSelected(column: string): boolean {
    return this.selectedAggregateColumns.includes(column);
  }

  toggleColumnSelection(column: string): void {
    this.selectedAggregateColumns = this.isColumnSelected(column)
      ? this.selectedAggregateColumns.filter((selectedColumn) => selectedColumn !== column)
      : [...this.selectedAggregateColumns, column];
  }

  showAggregates(): void {
    this.appliedAggregateColumns = [...this.selectedAggregateColumns];
  }

  clearAggregates(): void {
    this.selectedAggregateColumns = [];
    this.appliedAggregateColumns = [];
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'ascending' ? 'descending' : 'ascending';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'ascending';
    }
    this.currentPage = 1;
  }

  getSortIndicator(column: string): string {
    if (this.sortColumn !== column) {
      return '<>';
    }
    return this.sortDirection === 'ascending' ? '^' : 'v';
  }

  private compareValues(left: unknown, right: unknown): number {
    const leftNumber = this.toNumber(left);
    const rightNumber = this.toNumber(right);
    let comparison: number;

    if (leftNumber !== null && rightNumber !== null) {
      comparison = leftNumber - rightNumber;
    } else {
      comparison = this.formatValue(left).localeCompare(this.formatValue(right), undefined, { numeric: true, sensitivity: 'base' });
    }

    return this.sortDirection === 'ascending' ? comparison : -comparison;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value !== 'number' && typeof value !== 'string') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  changeTable(tableName: string): void {
    this.selectedTable = tableName;
  }

  setSearchTerm(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
  }

  setPageSize(pageSize: string): void {
    this.pageSize = Number(pageSize);
    this.currentPage = 1;
  }

  scrollTo(element: HTMLElement): void {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  previousPage(): void {
    this.currentPage = Math.max(1, this.currentPage - 1);
  }

  nextPage(): void {
    this.currentPage = Math.min(this.totalPages, this.currentPage + 1);
  }

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.errorMessage = '';
    this.currentPage = 1;
    const tableToLoad = this.selectedTable;
    const isNewTable = tableToLoad !== this.loadedTable;
    const requestId = ++this.latestRequestId;

    this.api.getNeonData(tableToLoad, 1000).subscribe({
      next: (data) => {
        if (requestId !== this.latestRequestId) {
          return;
        }
        const sanitized = this.sanitizeRows(data);
        this.rows = sanitized;
        this.headers = sanitized.length ? Object.keys(sanitized[0]) : [];
        this.loadedTable = tableToLoad;
        if (isNewTable) {
          this.searchTerm = '';
        }
        this.selectedAggregateColumns = [];
        this.appliedAggregateColumns = [];
        this.loading = false;
      },
      error: (err) => {
        if (requestId !== this.latestRequestId) {
          return;
        }
        this.loading = false;
        this.errorMessage = err?.error?.detail || 'Failed to load Neon data.';
      },
    });
  }
}
